import styled, { css, keyframes } from 'styled-components';

import { BREAKPOINTS } from '@shared/styled-system';

import bronzeAvatar from '@assets/images/coin-race/race/winners/bronze-avatar.png';
import bronzeStar from '@assets/images/coin-race/race/winners/bronze-star.png';
import bronzeBg from '@assets/images/coin-race/race/winners/bronze.png';
import goldBg from '@assets/images/coin-race/race/winners/gold.png';
import goldenAvatar from '@assets/images/coin-race/race/winners/golden-avatar.png';
import goldenStar from '@assets/images/coin-race/race/winners/golden-star.png';
import silverAvatar from '@assets/images/coin-race/race/winners/silver-avatar.png';
import silverStar from '@assets/images/coin-race/race/winners/silver-star.png';
import silverBg from '@assets/images/coin-race/race/winners/silver.png';

const SHADOW_COLORS = {
	1: 'rgba(244, 230, 131, 0.9)',
	2: 'rgba(194, 212, 225, 0.9)',
	3: 'rgba(161, 80, 61, 0.9)'
};

const STAR_SIZES = {
	default: '62px',
	small: '50px'
};

const slowPulse = keyframes`
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}

  25% {
    transform: scale(1.07);
  }

	50% {
		transform: scale(1.06);
		opacity: 0.85;
	}
`;

const getBackgroundImage = (place: number): string => {
	switch (place) {
		case 1:
			return goldBg;
		case 2:
			return silverBg;
		default:
			return bronzeBg;
	}
};

const getAvatarImage = (place: number): string => {
	if (place === 1) return goldenAvatar;
	if (place === 2) return silverAvatar;
	return bronzeAvatar;
};

const getStarImage = (place: number): string => {
	if (place === 1) return goldenStar;
	if (place === 2) return silverStar;
	return bronzeStar;
};

const getAvatarShadowStyles = (place: number) => {
	const color = SHADOW_COLORS[(place as keyof typeof SHADOW_COLORS) || 3];

	if (!color) return css``;

	return css`
		filter: drop-shadow(0px 0px 70px ${color});
	`;
};

export const TopWinnersContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 24px;

	@media (${BREAKPOINTS.MOBILE}) {
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}
`;

export const Wrapper = styled.div<{ $place: number }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 343px;
	position: relative;

	@media (${BREAKPOINTS.MOBILE}) {
		width: 100%;
		margin-top: 24px;
	}
`;

export const AvatarContainer = styled.div`
	width: 100%;
	height: 62px;
	display: flex;
	justify-content: center;
	margin-bottom: 8px;
	position: relative;
	z-index: 2;
`;

export const Avatar = styled.div<{ $place: number; $isMe?: boolean }>`
	width: 100%;
	border-radius: 50%;

	background-image: url(${({ $place }) => getAvatarImage($place)});
	background-position: center;
	background-repeat: no-repeat;
	background-size: contain;

	${({ $place }) => getAvatarShadowStyles($place)}

	${({ $isMe }) =>
		$isMe &&
		css`
			animation: ${slowPulse} 2s ease-in-out infinite;
		`}
`;

export const Username = styled.div<{ $isMe?: boolean }>`
	font-size: 16px;
	font-weight: 400;
	color: ${({ theme, $isMe }) => ($isMe ? theme.colors.text.link : theme.colors.text.alwaysWhite)};
	text-align: center;
	margin-bottom: -20px;
	z-index: 2;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
	max-width: 90%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	height: 20px;
`;

export const CardBox = styled.div<{ $place: number }>`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;

	width: 100%;
	height: 200px;
	padding-top: 30px;

	background-image: url(${({ $place }) => getBackgroundImage($place)});
	background-position: center;
	background-repeat: no-repeat;
	background-size: 100% 100%;
`;

export const StarIcon = styled.div<{ $place: number }>`
	flex-shrink: 0;
	width: ${({ $place }) => ($place === 3 ? STAR_SIZES.small : STAR_SIZES.default)};
	height: ${({ $place }) => ($place === 3 ? STAR_SIZES.small : STAR_SIZES.default)};

	background-image: url(${({ $place }) => getStarImage($place)});
	background-position: center;
	background-repeat: no-repeat;
	background-size: contain;

	margin-top: ${({ $place }) => ($place === 3 ? '8px' : '4px')};
`;

export const InfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-end;
	flex-grow: 1;
	width: 100%;
	padding: 0 16px 12px;
	gap: 6px;
`;

export const PrizeContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
`;

export const PrizeLabel = styled.span`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.text.subtitle};
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

export const PrizeValue = styled.span`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.text.alwaysWhite};
	text-align: center;
`;

export const FooterRow = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 8px;

	background: ${({ theme }) => theme.colors.transparent.light5};
	border-radius: 4px;
`;

export const FooterItem = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

export const FooterLabel = styled.span`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.text.subtitle};
	margin-bottom: 2px;
`;

export const FooterValue = styled.span`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.text.alwaysWhite};
`;

export const Divider = styled.div`
	width: 1px;
	height: 24px;
	background-color: ${({ theme }) => theme.colors.transparent.light5};
`;
